import type { DefaultError } from "@tanstack/query-core";
import {
  type QueryFunction,
  type SkipToken,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { useCallback, useMemo } from "react";

import { isFunction } from "@/types/utils";

import { useUserContext } from "@/context/user.context";

export const useAuthedQuery = <
  QueryFnData,
  TError = DefaultError,
  ReturnData = QueryFnData,
>({
  queryFn,
  ...config
}: UseQueryOptions<QueryFnData, TError, ReturnData>) => {
  const { user } = useUserContext();
  const enabled = useMemo(
    () => !!user && config.enabled,
    [config.enabled, user],
  );
  // We wrap the query function to allow New Relic to capture errors that it
  // otherwise misses.
  const wrappedFn = useCallback<QueryFunction<QueryFnData>>(
    (...args) => {
      if (!isFunction(queryFn)) {
        // This error should be unreachable based on the condition in
        // wrappedQueryFn's definition.
        throw new Error("Query function is not a function");
      }

      const response = queryFn(...args);

      if (response instanceof Promise) {
        return response.catch((err: AxiosError) => {
          window.newrelic?.noticeError(err, {
            code: err.code,
            stack: err.stack,
            status: err.status,
            url: err.request?.responseURL,
          });

          throw err;
        });
      }

      return response;
    },
    [queryFn],
  );

  const wrappedQueryFn: QueryFunction<QueryFnData> | SkipToken | undefined =
    isFunction(queryFn) ? wrappedFn : queryFn;

  return useQuery({
    ...config,
    enabled,
    queryFn: wrappedQueryFn,
  });
};
